import { useLocalisation } from '@archipro-website/localisation';
import { ReactComponent as ArchiproWordmark } from '@rocco/icons/svg/logo-text.svg';

import { cn } from '@rocco/utils/cn';
import { lookbookMarketName } from './lookbook-market';

interface LookbookCoverPreviewProps {
    title: string;
    professional: string;
    /** The banner the cover's photograph is drawn from. */
    imageSrc?: string | null;
    className?: string;
}

/**
 * The lookbook's cover, drawn in the page rather than rendered from the PDF:
 * the same A4 page, pale ground, wordmark and imprint the document opens on.
 *
 * The photograph is the one departure. The printed cover desaturates it — see
 * the lookbook route, which asks the image service for it in black and white —
 * where this one keeps its colour, because on the page it has a job the
 * printed cover does not: standing off a frame that is itself greyscale.
 *
 * Every measure is the PDF's own point value as a share of the page's 595pt
 * width, in container units, so the cover keeps its proportions at whatever
 * width it is set.
 */
export const LookbookCoverPreview = ({
    title,
    professional,
    imageSrc,
    className,
}: LookbookCoverPreviewProps) => {
    const { country } = useLocalisation();

    return (
        <div
            aria-hidden="true"
            className={cn(
                '@container relative aspect-[595/842] overflow-hidden bg-[#f1f0f3] text-black',
                className,
            )}
        >
            <ArchiproWordmark className="absolute top-[4.2cqw] left-[1.68cqw] h-auto w-[96.64cqw] [&_path]:fill-black" />
            <div className="absolute inset-x-[10cqw] top-[20.17cqw] flex flex-col items-center text-center font-sans font-bold uppercase">
                <p className="m-0 text-[1.613cqw] leading-[1.06] tracking-[-0.084cqw]">
                    Project look book featuring
                    <br />
                    {title} by {professional}
                </p>
                <p className="m-0 mt-[7.56cqw] text-[1.613cqw] leading-[0.87] tracking-[-0.084cqw]">
                    {lookbookMarketName(country)}
                    <br />
                    {new Date().getFullYear()}
                </p>
            </div>
            {/* Sized outright: an image takes its height from its own
                proportions and ignores a bottom inset.

                In colour, against the desaturated frame it stands on: the
                cover is the one thing on that picture the reader is meant to
                reach for, and grey on grey it had nothing to say so. */}
            {imageSrc ? (
                <img
                    src={imageSrc}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute top-[57.4%] left-[1.68cqw] h-[calc(42.6%-1.68cqw)] w-[96.64cqw] object-cover"
                />
            ) : null}
        </div>
    );
};
